import React, { useRef, useState } from 'react';
import { DELETE } from 'Pages/GMS/ISR/ISRConstants';
import Menu from 'Components/DesignSystem/Menu/Menu';
import { MoreVertical } from 'Components/DesignSystem/Iconography/Icons';
import { FOCUS_RING_INSET } from 'Components/DesignSystem/focusRing';
import { useDismissable } from 'Components/DesignSystem/useDismissable';

type TDeleteDataModelProps = {
    onContextMenuOptionClick: () => void;
    /**
     * Base for this instance's `id`/`data-testid` pair. Every row in the Model
     * Explorer already receives a unique `id`; passing it down is what makes the
     * trigger addressable. See the note on testids below.
     */
    id?: string;
    menuId?: string;
    deleteButtonId?: string;
    /**
     * Runs on the trigger's click, BEFORE the menu opens.
     *
     * This reproduces behaviour that used to happen by accident. The trigger was a
     * `div role="button"` nested inside the row's own `div role="button"`, so its
     * click bubbled and the row's `onSelect` fired too — opening a field or category
     * row's ⋮ also selected that row and swapped the right-hand pane. `SubModelItem`
     * was the exception: it wrapped this component in a div with
     * `onClick={e => e.stopPropagation()}`, so a sub-model header's ⋮ did NOT select.
     *
     * The trigger is a real `<button>` now and the row's target is a sibling, so
     * nothing bubbles anywhere. `FieldItem` and `ProductCategoryItem` pass their
     * `onSelect` here to keep the old behaviour exactly; `SubModelItem` passes
     * nothing, which is the old stopPropagation. Accidental before, explicit now —
     * unchanged either way.
     */
    onTriggerClick?: () => void;
}

/**
 * The Model Explorer's row overflow menu. Restyled onto the design system
 * 2026-08-30 — `okf/isr/e2e.md` § "The expandable list".
 *
 * **It was not reachable by keyboard.** The trigger was a `div role="button"` with
 * no `tabIndex` and no key handler, so it announced itself as a button and then
 * could not be focused or activated. It is a real `<button>` now, and the surface
 * is DS `Menu`, which brings the roving tabindex and `role="menu"` semantics.
 *
 * **Escape closes it.** The old `useClickOutside` listened for `mousedown` only —
 * there was no key handling at all, so an open menu could only be dismissed with
 * the mouse. `useDismissable` adds Escape, and restores focus to the trigger.
 *
 * **Delete is a button, not a link.** It was `<Link to="#">`, and the handler
 * called `stopPropagation()` but never `preventDefault()`. react-router's `Link`
 * runs the consumer's `onClick` and then navigates unless `defaultPrevented`
 * (`react-router/dist/.../chunk-62JRHF6Z.mjs`, `handleClick`), so **every delete
 * pushed a history entry** — delete three fields and Back needed three presses to
 * leave the page. A menu item is a button; nothing navigates.
 *
 * **Testids are derived from `id`.** The trigger's was the literal
 * `components-deletedatamodel-div-1` on every instance: measured, ONE expanded
 * sub-model with six children rendered **seven** elements carrying it, so no spec
 * could address a row's menu. `id` is optional only because the dead
 * `CategoryItem.tsx` still mounts this without one.
 */
const DeleteDataModel = ({ onContextMenuOptionClick, id, menuId, deleteButtonId, onTriggerClick }: TDeleteDataModelProps) => {
    const [showMenu, setShowMenu] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const baseId = id ?? 'delete-data-model';
    const triggerId = `${baseId}-menu-btn`;
    const resolvedMenuId = menuId ?? `${baseId}-menu`;
    const resolvedDeleteId = deleteButtonId ?? `${baseId}-menu-btn-delete`;

    useDismissable({
        isOpen: showMenu,
        onDismiss: () => setShowMenu(false),
        isInside: (target) => !!containerRef.current?.contains(target),
    });

    const handleClick = () => {
        onTriggerClick?.();
        setShowMenu((prev) => !prev);
    };

    const handleDeleteClick = () => {
        onContextMenuOptionClick();
        setShowMenu(false);
    };

    return (
        <div ref={containerRef} className='tw-relative tw-shrink-0'>
            {/*
                28×32 hit area, unchanged — measured before and after. `aria-label`
                was already right; `aria-haspopup`/`aria-expanded` are new, and are
                what tell a screen-reader user that this opens something.
            */}
            <button
                type="button"
                id={triggerId}
                data-testid={triggerId}
                aria-label="More options"
                aria-haspopup="menu"
                aria-expanded={showMenu}
                aria-controls={showMenu ? resolvedMenuId : undefined}
                onClick={handleClick}
                className={`tw-flex tw-items-center tw-justify-center tw-py-2 tw-px-3 tw-rounded-ctl tw-text-ink-tertiary hover:tw-bg-surface-hover-strong hover:tw-text-ink motion-safe:tw-transition-colors ${FOCUS_RING_INSET}`}
            >
                {/*
                    DS `MoreVertical`, not `GmsIcons`' `OverFlowIcon`: that one hardcodes
                    `fill="#707275"` and takes no props, so the glyph could not follow the
                    button's hover colour. See `Iconography/Icons.tsx`.
                */}
                <MoreVertical aria-hidden="true" />
            </button>
            {showMenu ? (
                <div
                    id={resolvedMenuId}
                    /*
                        `right-0 top-full` — BELOW the trigger, where a menu belongs. It
                        was `bottom-1`, which laid the popover over the bottom two-thirds
                        of its own row. Measured: it is not clipped by the rail's
                        `overflow-y-auto` in either position (160×37 inside a 340px rail),
                        so this is a placement fix, not a clipping one.
                    */
                    className='tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-w-40 tw-z-50'
                >
                    <Menu
                        variant="compact"
                        data-testid={resolvedMenuId}
                        items={[
                            {
                                id: 'delete',
                                label: DELETE,
                                tone: 'danger',
                                onClick: handleDeleteClick,
                                'data-testid': resolvedDeleteId,
                            },
                        ]}
                    />
                </div>
            ) : null}
        </div>
    );
}

export default DeleteDataModel
